import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import PasswordInput from '@/components/Forms/PasswordInput';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import { useRequest } from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';
import { settingsButtonClass } from './SettingsSection';

// Types
type ChangePassword = { oldPassword: string; newPassword: string };

type ChangePasswordData = API.WithData<ChangePassword>;

type ChangePasswordState = {
  message: string;
};

// API requests TODO
const changePasswordRequest: RequestSchema<ChangePasswordState, ChangePasswordData> = {
  name: 'changePasswordRequest',
  input: '/api/v1/user/update/password',
  method: 'POST',
};

export default function ChangePasswordForm() {
  const form = useForm<WithFormErrors<ChangePassword>>();

  // Request hooks
  const request = useRequest(changePasswordRequest, { manual: true, hideAlert: true });

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isSubmitted]);

  // Handlers
  const handleFormSubmit = useFormSubmit(
    {
      form: form,
      callback: async (body) => {
        setIsSubmitted(false);
        const result = await request.run({ data: body }, { throwOnRun: true });

        if (result?.message === 'API.INCORRECT.OLD.PASSWORD') {
          setIsSubmitted(false);
          form.setError('oldPassword', {
            type: 'manual',
            message: 'Current password is incorrect.',
          });
        } else {
          setIsSubmitted(true);
          form.reset();
        }
      },
    },
    [],
  );

  return (
    <form onSubmit={handleFormSubmit} method="POST" className="space-y-6">
      <FormControl
        form={form}
        name="oldPassword"
        label="Current password"
        wrap={PasswordInput}
        required
        autoComplete="off"
      />

      <FormControl
        form={form}
        name="newPassword"
        label="New password"
        wrap={PasswordInput}
        required
        minLength={8}
        autoComplete="new-password"
      />

      <FormErrors
        form={form}
        request={request}
        rewrite={(err) => {
          if (err.code === 404) {
            return <>Incorrect email/username or password.</>;
          }

          return err.toString();
        }}
      />

      <div className="flex items-center space-x-4">
        <FormSubmitButton
          disabled={request.loading || !form.formState.isDirty}
          loading={request.loading}
          inline
          className={settingsButtonClass}
        >
          Change password
        </FormSubmitButton>
        <div
          className={clsx(
            'leading-4 text-zinc-500 transition-opacity duration-500',
            isSubmitted ? 'opacity-100' : 'opacity-0',
          )}
        >
          Password changed successfully!
        </div>
      </div>
    </form>
  );
}
